import { useNavigate } from "react-router-dom";
import { useSelector } from "store/useSelector";
import { uniqBy } from "lodash";
import {
  CATEGORY_SEPARATOR_ROW_SPAN,
  ProcessTilesPanelStyles,
} from "./ProcessTilesPanelStyles";
import {
  getPgKey,
  useGetPageFromPath,
  useIsSerialProcess,
  useSummaryGroup,
  useVisibleGroups,
  useVisibleProcesses,
  useWorkScheduleIdOnCurrentPage,
} from "../../../../utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { getIsIe11, PNL_ORDER } from "../../../../constants/constants";
import { isEqual } from "lodash";
import type {
  DataDumpAggregateType,
  Process,
  ProcessGroup,
  ProductionSegmentType,
} from "types/types";
import { useSpring, animated } from "@react-spring/web";
import { useTileSize } from "pages/useTileSize";
import { useCachedProcessGroups, useGetLineRole } from "./processTileUtils";
import {
  INITIAL_PROCESS_TILES_FILTER_STATE,
  useProcessTilesFilterState,
} from "pages/DashboardPages/ProcessTilesControls/useProcessTilesFilterState";
import { PnlCategoriesEnum } from "types/enums";
import { useShouldUseDataDumpsForProcessTile } from "utils/customHooks/whichServerHooks";
import { calcMetric } from "utils/metrics";
import { useGetOutputsAndTargets } from "components/AheadBehind/aheadBehindUtils";
import {
  Fragment,
  lazy,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useMount, usePrevious } from "react-use";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import RenderIfVisible from "react-render-if-visible";
import { useIsDarkMode, useWasLastPageOperatorPage } from "store/atoms";
import { darkModeModalBackgroundColor } from "constants/colors";
import { useObserveComponentLoadTime } from "App/FlightPerformanceObserver";
import {
  useAllWorkSchedulesPageProcesses,
  useGroupTilesFilterState,
  useSetGroupTilesFilterStateFromUrlOnMount,
} from "../GroupTile/groupTilesUtils";
import { mapEntries } from "@raven/shared/utils";
import { isVariationOfAll } from "components/FlightTrends/TrendsFilters/filtersDrawerUtils";
const TileLoaderSkeleton = lazy(() => import("../TileLoaderSkeleton"));
const ProcessTile = lazy(() => import("./ProcessTile"));
const ALL_WORKSCHEDULES_PROCESS_GROUP: ProcessGroup = {
  groupSortIdx: "0",
  summaryGroup: "all",
  shortName: "all",
  longName: "all",
  processes: [],
  count: 0,
  showCountNotMetricOnGroupTile: false,
  callStart: 0,
  isIdleUntagged: false,
  processesSortOrder: [],
  output: 0,
  outputLabel: "",
  ckey: "",
  id: "ALL_WORKSCHEDULES_PROCESS_GROUP",
};
function ProcessTilesPanel(): React.JSX.Element {
  const isSerialProcessIndicatorOpen = useSelector(
    state => state.legacy.isSerialProcessIndicatorOpen
  );
  const { isAllWorkSchedulesPage } = useGetPageFromPath();
  const isSerialProcess = useIsSerialProcess();

  const group = useSummaryGroup();
  const processGroups = useVisibleGroups();

  // for now, we only show one Process Group on the ProcessTilesPanel page
  const latestProcessGroup = uniqBy(processGroups, "summaryGroup") // filter out any duplicates of the same group
    // filter out any other group's processes
    // or duplicates of the same group
    // (happens when navigating quickly on slow connection)
    .find(pg => pg.summaryGroup === group);

  const ckey = useCustomerKey();
  const [cachedProcessGroups] = useCachedProcessGroups();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const cachedProcessGroup = cachedProcessGroups[ckey]?.[workScheduleId]?.find(
    pg => pg.summaryGroup === group
  );
  const processGroup = isAllWorkSchedulesPage
    ? ALL_WORKSCHEDULES_PROCESS_GROUP
    : (latestProcessGroup ?? cachedProcessGroup);

  useObserveComponentLoadTime({
    name: "Process Tiles Panel",
    loading: !processGroup,
  });
  useSetGroupTilesFilterStateFromUrlOnMount();

  return (
    <>
      {!processGroup ? (
        <div
          style={{
            paddingTop: "1em",
          }}
        >
          <TileLoaderSkeleton />
        </div>
      ) : (
        <ProcessTilesPanelContent
          processGroup={processGroup}
          isSerialProcessIndicatorOpen={
            isSerialProcess && isSerialProcessIndicatorOpen
          }
        />
      )}
    </>
  );
}

export default ProcessTilesPanel;

const AnimatedProcessTilesPanelStyles = animated(ProcessTilesPanelStyles);

// tslint:disable-next-line: cognitive-complexity
function ProcessTilesPanelContent({
  processGroup,
}: {
  processGroup: ProcessGroup;
  isSerialProcessIndicatorOpen: boolean;
}) {
  const {
    groupSortIdx,
    processes: latestProcesses,
    summaryGroup,
  } = processGroup;
  const [cachedProcessGroups] = useCachedProcessGroups();
  const allGroups = useSelector(state => state.legacy.allGroups);
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const customerKey = useCustomerKey();
  const processesInSelectedGroups = useAllWorkSchedulesPageProcesses();
  const { isAllWorkSchedulesPage } = useGetPageFromPath();
  const cachedProcesses = useMemo(
    () =>
      cachedProcessGroups[customerKey]?.[workScheduleId]?.find(
        pg => pg.summaryGroup === summaryGroup
      )?.processes ?? [],
    [summaryGroup, customerKey, workScheduleId, cachedProcessGroups]
  );
  const processes = useMemo(
    () =>
      isAllWorkSchedulesPage
        ? processesInSelectedGroups
        : uniqBy([...latestProcesses, ...cachedProcesses], p => p.id),
    [
      isAllWorkSchedulesPage,
      processesInSelectedGroups,
      latestProcesses,
      cachedProcesses,
    ]
  );
  const selectedProcessIdsRef = useSelector(
    state => state.legacy.selectedProcessIdsRef,
    isEqual
  );
  const pgKey = getPgKey(processGroup.shortName);

  const segments = useSelector(state => state.legacy.segments, isEqual);
  const isIE = getIsIe11();
  const getLineRole = useGetLineRole(pgKey);
  const [filterState] = useProcessTilesFilterState();
  useSyncProcessTilesFilterStateToUrl();
  useResetFiltersOnCustomerOrGroupChange();
  const [
    {
      general: { redirectFromHubletViewPageToGridPage, hideProcessTiles },
    },
  ] = useFlightConfig();
  const sortProcessesByFilterState = useSortProcesses();
  const [{ selectedGroupNames }] = useGroupTilesFilterState();
  const allSelectedGroups = useMemo(() => {
    // if we're on an "All" group page, return allGroups except for the "all" group
    if (isVariationOfAll(summaryGroup)) {
      return allGroups.filter(pg => !isVariationOfAll(pg.summaryGroup));
    }
    // else return only the selected groups
    return allGroups.filter(pg => selectedGroupNames.includes(pg.summaryGroup));
  }, [summaryGroup, allGroups, selectedGroupNames]);
  const processesWithData: ProcessWithData[] = useMemo(() => {
    const processesForCustomer: Process[] = processes
      // filter out any other customer's processes
      // (only applies to people who can access multiple customers)
      .filter(proc => proc.ckey === customerKey);

    const processesFilteredByProcessAndCategoryAndTag: Process[] =
      processesForCustomer.filter(proc => {
        const processSegments = segments[proc.id];
        let matchesCategory = true;
        const currentSegment = processSegments?.[processSegments.length - 1];
        if (currentSegment) {
          matchesCategory = filterState.filterByCategory.includes(
            currentSegment.pnl.category as PnlCategoriesEnum
          );
        }

        const isFilteredByProcess =
          filterState.selectedProcessNames.length !== 0 &&
          filterState.selectedProcessNames.length !== processes.length &&
          !filterState.selectedProcessNames.includes("SELECT_ALL");

        const matchesSelectedProcessNames =
          !isFilteredByProcess ||
          filterState.selectedProcessNames.includes(proc.name);

        const isFilteredBySearchText = filterState.searchText;
        const matchesSearchText =
          isFilteredBySearchText &&
          proc.name
            .toLowerCase()
            .includes(String(filterState.searchText).toLowerCase());

        const isFilteredByTags =
          filterState.filterByTagNames &&
          filterState.filterByTagNames.length > 0;
        const matchesAnyTag = isFilteredByTags
          ? currentSegment?.tag?.name &&
            filterState.filterByTagNames?.includes(currentSegment.tag.name)
          : true;

        return (
          matchesCategory &&
          matchesAnyTag &&
          (isFilteredByProcess
            ? matchesSelectedProcessNames || matchesSearchText
            : isFilteredBySearchText
              ? matchesSearchText
              : true)
        );
      });

    const processesWithIdx: (Process & { idx: number })[] =
      processesFilteredByProcessAndCategoryAndTag
        // at this point note the idx, which will be used to link to the process page
        .map((proc, idx) => ({
          ...proc,
          idx,
        }));

    const processesSortedByMidgard: (Process & { idx: number })[] =
      processesWithIdx
        // if using midgard processes, sort by processesSortOrder
        .sort((a, b) => {
          if (processGroup.processesSortOrder) {
            return (
              processGroup.processesSortOrder.indexOf(a.id) -
              processGroup.processesSortOrder.indexOf(b.id)
            );
          }
          return 0;
        });

    const processesWithGroupId: ProcessWithGroupId[] = processesSortedByMidgard
      // add the groupId to each process
      .map(proc => {
        // sort by the group name
        if (!allSelectedGroups.length) {
          return proc;
        }

        const processGroup = allSelectedGroups.find(pg =>
          pg.processes.map(p => p.id).includes(proc.taps[0].machemetryId)
        );

        return {
          ...proc,
          groupId: processGroup?.id,
        };
      });
    const processesGrouped = processesWithGroupId.reduce<ManyProcessesGrouped>(
      (acc, cur) => {
        if (filterState.groupBy?.customKey === "pnlCategory") {
          // group by pnlCategory
          const processSegments = segments[cur.id];
          const lastSegment = processSegments
            ? processSegments[processSegments.length - 1]
            : null;
          const pnlCategory = lastSegment?.pnl.category;
          if (pnlCategory) {
            if (!acc[pnlCategory]) {
              acc[pnlCategory] = [];
            }
            acc[pnlCategory].push(cur);
          } else {
            acc["ungrouped"] = acc["ungrouped"] || [];
            acc["ungrouped"].push(cur);
          }
          return acc;
        } else if (filterState.groupBy?.customKey === "group") {
          // group by groupId
          if (cur.groupId && !acc[cur.groupId]) {
            acc[cur.groupId] = [];
          }
          if (cur.groupId) {
            acc[cur.groupId].push(cur);
          } else {
            acc["ungrouped"] = acc["ungrouped"] || [];
            acc["ungrouped"].push(cur);
          }
          return acc;
        } else {
          acc["ungrouped"] = acc["ungrouped"] || [];
          acc["ungrouped"].push(cur);
          return acc;
        }
      },
      {} as ManyProcessesGrouped
    );

    const isUngrouped = !filterState.groupBy;

    const processesGroupedAndSorted = mapEntries<ManyProcessesGrouped>(
      processesGrouped,
      ([groupId, groupOfProcesses]: [string, ProcessWithGroupId[]]) => {
        // for each group, sort it by the groupBy order

        const groupSortedByGroup: ProcessWithGroupId[] = groupOfProcesses.sort(
          (a, b) => {
            if (isUngrouped || !filterState.groupBy) {
              return 0;
            }
            if (filterState.groupBy.customKey === "group") {
              const processGroupA = allSelectedGroups.find(pg =>
                pg.processes.find(p => p.id === a.id)
              );

              const processGroupB = allSelectedGroups.find(pg =>
                pg.processes.find(p => p.id === b.id)
              );

              if (!processGroupA || !processGroupB) {
                return 0;
              }
              return filterState.groupBy.order === "asc"
                ? processGroupA.summaryGroup.localeCompare(
                    processGroupB.summaryGroup
                  )
                : processGroupB.summaryGroup.localeCompare(
                    processGroupA.summaryGroup
                  );
            }
            return 0;
          }
        );

        // then, sort by filterState.sortBy -- if ungrouped, sort all together; if grouped, sort each group
        const groupSortedBySort: ProcessWithGroupId[] = isUngrouped
          ? groupSortedByGroup
          : groupSortedByGroup.sort((a, b) => sortProcessesByFilterState(a, b));

        const groupWithLastSegment: ProcessWithData[] = groupSortedBySort.map(
          process => {
            const isUnselected =
              selectedProcessIdsRef.current.length > 0 &&
              !selectedProcessIdsRef.current.includes(process.id);

            const processSegments = segments[process.id];
            const lastSegment =
              processSegments && processSegments.length > 0
                ? processSegments[processSegments.length - 1]
                : null;

            const messageTime = null; // TODO
            return {
              ...process,
              isUnselected,
              lineRole: process.lineRole ?? getLineRole(process.id),
              lastSegment,
              messageTime,
            };
          }
        );

        return [groupId, groupWithLastSegment];
      }
    );

    const processesGroupedAndSortedValues = Object.values(
      processesGroupedAndSorted as ManyProcessesWithDataGrouped
    )
      // sort the groups according to filterState.groupBy.order
      .sort((a, b) => {
        if (!filterState.groupBy) {
          return 0;
        } else if (filterState.groupBy.customKey === "pnlCategory") {
          const processSegmentsA = a[0] ? segments[a[0].id] : null;
          const lastSegmentA = processSegmentsA
            ? processSegmentsA[processSegmentsA.length - 1]
            : null;
          const pnlCategoryA = lastSegmentA?.pnl.category;

          const processSegmentsB = b[0] ? segments[b[0].id] : null;
          const lastSegmentB = processSegmentsB
            ? processSegmentsB[processSegmentsB.length - 1]
            : null;
          const pnlCategoryB = lastSegmentB?.pnl.category;
          if (pnlCategoryA && pnlCategoryB) {
            return filterState.groupBy.order === "asc"
              ? PNL_ORDER[pnlCategoryB] - PNL_ORDER[pnlCategoryA]
              : PNL_ORDER[pnlCategoryA] - PNL_ORDER[pnlCategoryB];
          }
        }
        const groupNameA = a[0].name;
        const groupNameB = b[0].name;
        // sort based on filterState.groupBy.order
        if (filterState.groupBy.order === "desc") {
          return groupNameA.localeCompare(groupNameB);
        } else {
          return groupNameB.localeCompare(groupNameA);
        }
      });
    let processesGroupedAndSortedFlat = processesGroupedAndSortedValues.flat();

    // since we're not grouped, we didn't sort the groups individually, so we need to sort all the processes together instead
    if (isUngrouped) {
      processesGroupedAndSortedFlat = processesGroupedAndSortedFlat.sort(
        (a, b) => sortProcessesByFilterState(a, b)
      );
    }

    return processesGroupedAndSortedFlat;
  }, [
    customerKey,
    filterState.filterByCategory,
    filterState.searchText,
    filterState.selectedProcessNames,
    filterState.groupBy,
    filterState.filterByTagNames,
    getLineRole,
    processGroup.processesSortOrder,
    processes,
    segments,
    selectedProcessIdsRef,
    sortProcessesByFilterState,
    allSelectedGroups,
  ]);

  const shouldSeparateCategories =
    filterState.groupBy && filterState.groupBy.customKey === "pnlCategory";

  const shouldSeparateGroups =
    filterState.groupBy && filterState.groupBy.customKey === "group";

  const shouldSeparate = shouldSeparateCategories || shouldSeparateGroups;

  const { tileWidth, tileHeight, tileAndContentsHeight, isLargeTiles } =
    useTileSize();
  const visibleProcesses = useVisibleProcesses();
  const springExpandCollapse = useSpring({
    // each row will have increased height when expanded (tileHeightMultiplier)
    ...(isIE
      ? {}
      : {
          gridAutoRows:
            tileAndContentsHeight *
            (shouldSeparate ? CATEGORY_SEPARATOR_ROW_SPAN : 1),
        }),
    config: {
      tension: 300,
      mass: 0.7,
    },
    immediate: visibleProcesses.length > 12,
  });

  const isAtLeastOneProcessWithLineRole = useMemo(
    () =>
      Boolean(
        processesWithData.find(p => {
          return Boolean(p.lineRole && p.lineRole !== "STANDARD");
        })
      ),
    [processesWithData]
  );
  const [isDarkMode] = useIsDarkMode();
  const loaderBackgroundColor = isDarkMode
    ? darkModeModalBackgroundColor
    : "hsl(0, 0%, 93%)";
  const navigate = useNavigate();
  const [wasLastPageOperatorPage] = useWasLastPageOperatorPage();
  return (
    <div className="tilesWrapper">
      <AnimatedProcessTilesPanelStyles
        $isDarkMode={isDarkMode}
        $isLargeTiles={isLargeTiles}
        style={springExpandCollapse}
        data-component-name={"Process Tiles Panel"}
        {...{ $tileWidth: tileWidth, $tileHeight: tileHeight }}
        key={`panel-${groupSortIdx}`}
        className={`${isIE ? "internetExplorer" : ""}`}
        $loaderBackgroundColor={loaderBackgroundColor}
        $shouldSeparate={shouldSeparate}
      >
        {processesWithData.map((process, idx) => {
          const hideProcessTile = (hideProcessTiles ?? []).includes(process.id);
          // need to navigate using idx of process before it was filtered:
          const originalProcessIdx = processes
            .sort((a, b) => {
              if (!processGroup.processesSortOrder) {
                return 0;
              }
              return (
                processGroup.processesSortOrder.indexOf(a.id) -
                processGroup.processesSortOrder.indexOf(b.id)
              );
            })
            .findIndex(p => p.id === process.id);

          const isLastProcessInThisCategory =
            shouldSeparateCategories &&
            idx + 1 < processesWithData.length &&
            process.lastSegment?.pnl.category &&
            process.lastSegment?.pnl.category !==
              processesWithData[idx + 1].lastSegment?.pnl.category;

          const isFirstProcessInThisGroup =
            shouldSeparateGroups &&
            idx + 1 < processesWithData.length &&
            process.groupId &&
            (!processesWithData[idx - 1] ||
              process.groupId !== processesWithData[idx - 1].groupId);

          const thisProcessGroup = allSelectedGroups.find(
            group =>
              group.id !== "ALL_WORKSCHEDULES_PROCESS_GROUP" &&
              group.summaryGroup.toLowerCase() !== "all" &&
              group.processes.find(p => p.id === process.id)
          );

          return hideProcessTile ? null : (
            <Fragment
              key={`tile-container-${process.name}-${process.id}-idx-${idx}`}
            >
              {isFirstProcessInThisGroup && (
                <div className="group-separator">
                  <div className="group-separator-text">
                    {thisProcessGroup?.summaryGroup}
                  </div>
                </div>
              )}
              <div
                className="tile-renderifvisible-container"
                style={
                  shouldSeparate
                    ? {
                        gridRow: `span ${1 / CATEGORY_SEPARATOR_ROW_SPAN}`,
                      }
                    : {}
                }
              >
                <RenderIfVisible
                  placeholderElementClass="tile-container-placeholder"
                  initialVisible={processesWithData.length <= 32} // start visible (if there aren't too many) so we can quickly click back and forth into tiles without waiting for initial render
                >
                  <div
                    className={`tile-container${
                      process.isUnselected ? " unselected-process" : ""
                    }`}
                  >
                    <a
                      // instead of using react-router-dom Link, we can improve performance here by navigating inside a setTimeout -> startTransition, to let ProcessTile onClick handle state updates first before navigating
                      onClick={() => {
                        setTimeout(() => {
                          startTransition(() => {
                            let wsId = workScheduleId;
                            let sg = summaryGroup;
                            if (isAllWorkSchedulesPage) {
                              // set the url query workSchedule to match the process workSchedule
                              wsId = process.workScheduleConfig.id;
                              // set summaryGroup to match the process's group
                              const matchingGroup = allGroups.find(
                                group =>
                                  group.id !==
                                    "ALL_WORKSCHEDULES_PROCESS_GROUP" &&
                                  group.processes
                                    .map(p => p.id)
                                    .includes(process.id)
                              );
                              if (matchingGroup) {
                                sg = matchingGroup.summaryGroup;
                              }
                            }
                            const nextUrl = `/${customerKey}/${
                              redirectFromHubletViewPageToGridPage
                                ? "grid"
                                : wasLastPageOperatorPage
                                  ? "operator"
                                  : "dashboard"
                            }/${sg}/${originalProcessIdx}?workSchedule=${wsId}`;

                            navigate(nextUrl, {
                              state: {
                                newDashboardGe:
                                  process.lastSegment?.range.ge || null,
                                newDashboardMessageTime: process.messageTime,
                              },
                            });
                          });
                        }, 0);
                      }}
                    >
                      <ProcessTile
                        {...{ process, isAtLeastOneProcessWithLineRole }}
                      />
                    </a>
                  </div>
                </RenderIfVisible>
              </div>
              {isLastProcessInThisCategory && (
                <div className="category-separator" />
              )}
            </Fragment>
          );
        })}
      </AnimatedProcessTilesPanelStyles>
    </div>
  );
}

function useResetFiltersOnCustomerOrGroupChange() {
  const ckey = useCustomerKey();
  const prevCkey = usePrevious(ckey);
  const summaryGroup = useSummaryGroup();
  const prevSummaryGroup = usePrevious(summaryGroup);
  const [, setFilterState] = useProcessTilesFilterState();
  useEffect(() => {
    if (prevCkey && ckey !== prevCkey) {
      setFilterState(INITIAL_PROCESS_TILES_FILTER_STATE);
    }
  }, [ckey, prevCkey, setFilterState]);
  useEffect(() => {
    if (prevSummaryGroup && summaryGroup !== prevSummaryGroup) {
      setFilterState(INITIAL_PROCESS_TILES_FILTER_STATE);
    }
  }, [summaryGroup, prevSummaryGroup, setFilterState]);
}

/** 1. on mount, grab filterState from url and set to state
 *  2. on filterState change, update url
 */
function useSyncProcessTilesFilterStateToUrl() {
  const [filterState, setFilterState] = useProcessTilesFilterState();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const filterByCategory = urlSearchParams.get("filterByCategory");
  const urlTagNames = decodeURIComponent(
    urlSearchParams.get("filterByTags") ?? "[]"
  );
  const filterByTagNames = JSON.parse(urlTagNames);
  const sortBy = urlSearchParams.get("sortBy");
  const groupBy = urlSearchParams.get("groupBy");
  const searchText = urlSearchParams.get("searchText");
  const selectedProcessNames = urlSearchParams.get("selectedProcessNames");
  const filterByTopLosses = urlSearchParams.get("filterByTopLosses");
  const visibleProcesses = useVisibleProcesses();
  // 1. on mount, grab filterState from url and set to state

  useMount(() => {
    if (
      filterByCategory ||
      sortBy ||
      searchText ||
      selectedProcessNames ||
      filterByTopLosses
    ) {
      setFilterState({
        filterByCategory: filterByCategory
          ? (filterByCategory.split(",") as PnlCategoriesEnum[])
          : INITIAL_PROCESS_TILES_FILTER_STATE.filterByCategory,
        filterByTagNames: filterByTagNames
          ? filterByTagNames
          : INITIAL_PROCESS_TILES_FILTER_STATE.filterByTagNames,
        sortBy: sortBy
          ? JSON.parse(sortBy)
          : INITIAL_PROCESS_TILES_FILTER_STATE.sortBy,
        groupBy: groupBy
          ? JSON.parse(groupBy)
          : INITIAL_PROCESS_TILES_FILTER_STATE.groupBy,
        searchText: searchText
          ? searchText
          : INITIAL_PROCESS_TILES_FILTER_STATE.searchText,
        searchTextDisplay: searchText
          ? searchText
          : INITIAL_PROCESS_TILES_FILTER_STATE.searchTextDisplay,
        selectedProcessNames: selectedProcessNames
          ? selectedProcessNames.split(",")
          : INITIAL_PROCESS_TILES_FILTER_STATE.selectedProcessNames,
        filterByTopLosses: filterByTopLosses
          ? filterByTopLosses.split(",")
          : INITIAL_PROCESS_TILES_FILTER_STATE.filterByTopLosses,
      });
    }
  });

  const prevFilterState = usePrevious(filterState);
  const summaryGroup = useSummaryGroup();
  const prevSummaryGroup = usePrevious(summaryGroup);
  const isSameSummaryGroup =
    prevSummaryGroup && prevSummaryGroup === summaryGroup;
  const isCyStorybook = window.localStorage.IS_CY_STORYBOOK;

  const didFiltersChangeOnSamePage =
    !isCyStorybook &&
    isSameSummaryGroup &&
    prevFilterState &&
    !isEqual(filterState, prevFilterState);
  const navigate = useNavigate();
  // 2. on filterState change, update url
  useEffect(() => {
    if (!didFiltersChangeOnSamePage) {
      return;
    }
    const urlSearchParams = new URLSearchParams(window.location.search);
    // if the state is default, remove from url
    if (
      filterState.filterByCategory.join(",") !==
      INITIAL_PROCESS_TILES_FILTER_STATE.filterByCategory.join(",")
    ) {
      urlSearchParams.set(
        "filterByCategory",
        filterState.filterByCategory.join(",")
      );
    } else {
      urlSearchParams.delete("filterByCategory");
    }

    // filter by tags in url
    if (
      filterState.filterByTagNames &&
      filterState.filterByTagNames.length > 0 &&
      JSON.stringify(filterState.filterByTagNames) !==
        JSON.stringify(INITIAL_PROCESS_TILES_FILTER_STATE.filterByTagNames)
    ) {
      urlSearchParams.set(
        "filterByTags",
        encodeURIComponent(JSON.stringify(filterState.filterByTagNames))
      );
    } else {
      urlSearchParams.delete("filterByTags");
    }

    // filter by top losses in url
    if (
      filterState.filterByTopLosses &&
      filterState.filterByTopLosses.length > 0 &&
      JSON.stringify(filterState.filterByTopLosses) !==
        JSON.stringify(INITIAL_PROCESS_TILES_FILTER_STATE.filterByTopLosses)
    ) {
      urlSearchParams.set(
        "filterByTopLosses",
        encodeURIComponent(JSON.stringify(filterState.filterByTopLosses))
      );
    } else {
      urlSearchParams.delete("filterByTopLosses");
    }

    if (
      JSON.stringify(filterState.sortBy) !==
      JSON.stringify(INITIAL_PROCESS_TILES_FILTER_STATE.sortBy)
    ) {
      urlSearchParams.set("sortBy", JSON.stringify(filterState.sortBy));
    } else {
      urlSearchParams.delete("sortBy");
    }

    if (
      filterState.searchText !== INITIAL_PROCESS_TILES_FILTER_STATE.searchText
    ) {
      urlSearchParams.set("searchText", filterState.searchText || "");
    } else {
      urlSearchParams.delete("searchText");
    }

    if (
      filterState.selectedProcessNames.length > 0 &&
      filterState.selectedProcessNames.length !== visibleProcesses.length &&
      filterState.selectedProcessNames.sort().join(",") !==
        INITIAL_PROCESS_TILES_FILTER_STATE.selectedProcessNames.sort().join(",")
    ) {
      urlSearchParams.set(
        "selectedProcessNames",
        filterState.selectedProcessNames.join(",")
      );
    } else {
      urlSearchParams.delete("selectedProcessNames");
    }

    const newUrl = `${window.location.pathname}?${urlSearchParams}`;
    console.log(
      "useSyncProcessTilesFilterStateToUrl - navigating to newUrl",
      newUrl
    );
    navigate(newUrl, {
      replace: true,
    });
  }, [
    filterState,
    visibleProcesses.length,
    didFiltersChangeOnSamePage,
    navigate,
  ]);
}

type ProcessWithGroupId = Process & {
  idx: number;
  groupId?: string | null;
};
type ManyProcessesGrouped = {
  [key: string]: ProcessWithGroupId[];
};
type ProcessWithData = Process & {
  idx: number;
  lastSegment: ProductionSegmentType | null;
  messageTime: number | null | undefined;
  isUnselected: boolean;
  groupId?: string | null;
};
type ManyProcessesWithDataGrouped = {
  [key: string]: ProcessWithData[];
};

function useSortProcesses() {
  const [filterState] = useProcessTilesFilterState();
  const dataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );
  const rollupsMetricByProcess = useSelector(
    state => state.legacy.rollupsMetricByProcess
  );
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const shouldUseDataDumps = useShouldUseDataDumpsForProcessTile();
  const getOutputsAndTargets = useGetOutputsAndTargets();
  const segments = useSelector(state => state.legacy.segments, isEqual);

  return useCallback(
    (a, b) => {
      if (!filterState.sortBy) {
        return 0;
      }
      const dataDumpForProcessA: DataDumpAggregateType | undefined =
        dataDumpsFullRangeByProcess[a.id];
      const dataDumpForProcessB: DataDumpAggregateType | undefined =
        dataDumpsFullRangeByProcess[b.id];
      const rollupForProcessA = rollupsMetricByProcess[a.id];
      const rollupForProcessB = rollupsMetricByProcess[b.id];

      // sort by metrics like TEEP, Avail, Uptime, OEE
      if (
        filterState.sortBy.dataDumpKey &&
        [
          "availabilityPercent",
          "teepPercent",
          "oeePercent",
          "uptimePercent",
        ].includes(filterState.sortBy.dataDumpKey)
      ) {
        const metricA = calcMetric({
          rollupMetric: rollupForProcessA,
          dataDumpKey: filterState.sortBy.dataDumpKey,
          dataDumpsObjForRange: dataDumpForProcessA,
          isMidgardDDAEnabled,
        });
        const metricB = calcMetric({
          rollupMetric: rollupForProcessB,
          dataDumpKey: filterState.sortBy.dataDumpKey,
          dataDumpsObjForRange: dataDumpForProcessB,
          isMidgardDDAEnabled,
        });
        if (metricA === undefined && metricB === undefined) {
          return 0;
        }
        if (metricA === undefined) {
          return filterState.sortBy.order === "asc" ? -1 : 1;
        }
        if (metricB === undefined) {
          return filterState.sortBy.order === "asc" ? 1 : -1;
        }
        return filterState.sortBy.order === "asc"
          ? metricA - metricB
          : metricB - metricA;
      } else if (
        // sort by times like waitingTime, unplannedTime, notScheduledTime
        filterState.sortBy.dataDumpKey &&
        shouldUseDataDumps &&
        dataDumpForProcessA &&
        dataDumpForProcessB &&
        filterState.sortBy.dataDumpKey in dataDumpForProcessA &&
        filterState.sortBy.dataDumpKey in dataDumpForProcessB
      ) {
        const result =
          filterState.sortBy.order === "asc"
            ? Number(dataDumpForProcessA[filterState.sortBy.dataDumpKey]) -
              Number(dataDumpForProcessB[filterState.sortBy.dataDumpKey])
            : Number(dataDumpForProcessB[filterState.sortBy.dataDumpKey]) -
              Number(dataDumpForProcessA[filterState.sortBy.dataDumpKey]);

        return result;
      } else if (
        // sort by customKey - each requires special handling
        filterState.sortBy.customKey
      ) {
        if (filterState.sortBy.customKey === "aheadBehind") {
          const {
            netOutput: shiftOutputA,
            productionPotentialOutput: shiftCurrentTargetOutputA,
          } = getOutputsAndTargets(dataDumpForProcessA, a.id);

          const aheadBehindAmountA =
            shiftOutputA && shiftCurrentTargetOutputA
              ? shiftOutputA - shiftCurrentTargetOutputA
              : undefined;

          const {
            netOutput: shiftOutputB,
            productionPotentialOutput: shiftCurrentTargetOutputB,
          } = getOutputsAndTargets(dataDumpForProcessB, b.id);

          const aheadBehindAmountB =
            shiftOutputB && shiftCurrentTargetOutputB
              ? shiftOutputB - shiftCurrentTargetOutputB
              : undefined;

          if (
            aheadBehindAmountA === undefined ||
            aheadBehindAmountB === undefined
          ) {
            return 0;
          }
          return filterState.sortBy.order === "asc"
            ? aheadBehindAmountA - aheadBehindAmountB
            : aheadBehindAmountB - aheadBehindAmountA;
        } else if (filterState.sortBy.customKey === "scheduledTime") {
          const scheduledTimeA =
            dataDumpForProcessA.range.lt -
            dataDumpForProcessA.range.ge -
            Number(dataDumpForProcessA.notScheduledTime);
          const scheduledTimeB =
            dataDumpForProcessB.range.lt -
            dataDumpForProcessB.range.ge -
            Number(dataDumpForProcessB.notScheduledTime);
          return filterState.sortBy.order === "asc"
            ? scheduledTimeA - scheduledTimeB
            : scheduledTimeB - scheduledTimeA;
        } else if (filterState.sortBy.customKey === "pnlCategory") {
          const pnlCategoryA =
            segments[a.id]?.[segments[a.id].length - 1]?.pnl.category;
          const pnlCategoryB =
            segments[b.id]?.[segments[b.id].length - 1]?.pnl.category;
          if (pnlCategoryA === undefined || pnlCategoryB === undefined) {
            return 0;
          }
          return filterState.sortBy.order === "asc"
            ? PNL_ORDER[pnlCategoryB] - PNL_ORDER[pnlCategoryA]
            : PNL_ORDER[pnlCategoryA] - PNL_ORDER[pnlCategoryB];
        }
      }
      return 0;
    },
    [
      dataDumpsFullRangeByProcess,
      filterState.sortBy,
      isMidgardDDAEnabled,
      rollupsMetricByProcess,
      segments,
      shouldUseDataDumps,
      getOutputsAndTargets,
    ]
  );
}
